import type React from "react";

import type { ConnectedProjectDto, ProjectOverviewItemDto } from "@/api/types";
import type { ProjectFilterValues } from "@/components/ProjectFilter/ProjectFilter";
import { useProjectFilterOptions } from "@/components/ProjectFilter/useProjectFilterOptions";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

interface ProjectFilterDrawerFieldsProps {
  projects: (ConnectedProjectDto | ProjectOverviewItemDto)[];
  filters: ProjectFilterValues;
  onChange: (filters: ProjectFilterValues) => void;
  disabled?: boolean;
}

export function ProjectFilterDrawerFields({
  projects,
  filters,
  onChange,
  disabled,
}: ProjectFilterDrawerFieldsProps): React.ReactNode {
  const { t } = useTranslation();
  const { owners, maintainers, cities, amountUniqueOwners, amountUniqueMaintainers, amountUniqueCities } =
    useProjectFilterOptions(projects, filters);

  return (
    <Stack gap="md">
      {amountUniqueOwners > 1 && (
        <FormField label={t("page.portfolio.overview.filter.project-owner.label")}>
          <CheckboxMultiSelect<string>
            renderMoreSelected={(count) => t("common.label.x-more", { count })}
            placeholder={t("page.portfolio.overview.filter.project-owner.placeholder")}
            renderOption={(x) => x}
            keySelector={(x) => x}
            items={owners}
            selected={filters.owner ?? []}
            onChange={(value) => onChange({ ...filters, owner: value })}
            disabled={disabled}
            usePortal
          />
        </FormField>
      )}
      {amountUniqueMaintainers > 1 && (
        <FormField label={t("page.portfolio.overview.filter.project-maintainer.label")}>
          <CheckboxMultiSelect<string>
            renderMoreSelected={(count) => t("common.label.x-more", { count })}
            placeholder={t("page.portfolio.overview.filter.project-maintainer.placeholder")}
            renderOption={(x) => x}
            keySelector={(x) => x}
            items={maintainers}
            selected={filters.maintenance ?? []}
            onChange={(value) => onChange({ ...filters, maintenance: value })}
            disabled={disabled}
            usePortal
          />
        </FormField>
      )}
      {amountUniqueCities > 1 && (
        <FormField label={t("page.portfolio.overview.filter.project-city.label")}>
          <CheckboxMultiSelect<string>
            renderMoreSelected={(count) => t("common.label.x-more", { count })}
            placeholder={t("page.portfolio.overview.filter.project-city.placeholder")}
            renderOption={(x) => x}
            keySelector={(x) => x}
            items={cities}
            selected={filters.city ?? []}
            onChange={(value) => onChange({ ...filters, city: value })}
            disabled={disabled}
          />
        </FormField>
      )}
    </Stack>
  );
}
